import { Modal } from './Modal';
import { Sparkles, Award, ShoppingBag, Share2, Handshake } from 'lucide-react';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { cn } from '@/lib/utils'; // Ensure cn is imported or use string template
import { useTranslation } from 'react-i18next';
import { UsernameValuationCard } from '@/components/Shared/UsernameValuationCard';

interface CardDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    card: any;
    onBuy?: () => void;
    isP2P?: boolean;
}

export function CardDetailsModal({ isOpen, onClose, card, onBuy, isP2P }: CardDetailsModalProps) {
    const { impactOccurred } = useHapticFeedback();
    const { t } = useTranslation('store');
    const { t: tc } = useTranslation('common');

    if (!card) return null;

    const isStoreItem = !!onBuy;
    const isPaid = (card.price && Number(card.price) > 0);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={isStoreItem ? (isP2P ? t('makeOffer') : t('purchaseCard')) : t('cardDetails')} variant="bottom">
            <div className="space-y-6">
                {/* Header Card Visual */}
                <div
                    className="h-48 rounded-2xl relative overflow-hidden flex flex-col items-center justify-center border border-white/20 shadow-2xl transition-all"
                    style={{
                        background: `linear-gradient(135deg, ${card.base_color || card.color || '#3390EC'}40 0%, ${card.base_color || card.color || '#3390EC'}10 100%)`
                    }}
                >
                    <div className="absolute inset-0 backdrop-blur-md bg-[#dce0d9]/10" />

                    {/* Share Button */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            impactOccurred('light');
                            if (!card.id) return;
                            // Hardcoded fallback if INFO.json import fails or just strict string
                            const appUrl = import.meta.env.VITE_TELEGRAM_BOT_APP_URL;
                            const startParam = `card_${card.id}`;
                            const link = `${appUrl}?startapp=${startParam}`;
                            const text = t('shareCardText', { name: card.name || t('thisCard'), serial: card.serial_number ? ` #${card.serial_number}` : '' });
                            const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`;

                            // @ts-ignore
                            if (window.Telegram?.WebApp?.openTelegramLink) window.Telegram.WebApp.openTelegramLink(shareUrl);
                            else window.open(shareUrl, '_blank');
                        }}
                        className="absolute top-3 right-3 z-30 p-2 bg-black/30 hover:bg-black/50 backdrop-blur-md rounded-full transition-colors text-white/90"
                    >
                        <Share2 size={20} />
                    </button>

                    {/* Image */}
                    {card.image_filename || card.profileImage ? (
                        <div className="absolute inset-0 z-0">
                            <img src={card.image_filename || card.profileImage} className="w-full h-full object-cover opacity-50 blur-[2px] scale-110" />
                            <div className="absolute inset-0 bg-black/40" />
                        </div>
                    ) : null}

                    {/* Content */}
                    <div className="relative z-10 flex flex-col items-center">
                        {card.image_filename || card.profileImage ? (
                            <div className="w-20 h-20 rounded-xl overflow-hidden border-2 border-white/20 shadow-lg mb-3">
                                <img src={card.image_filename || card.profileImage} className="w-full h-full object-cover" />
                            </div>
                        ) : (
                            <Sparkles size={48} className="text-white opacity-80 mb-2" />
                        )}

                        <h2 className="text-3xl font-bold text-white tracking-widest uppercase font-bebas text-shadow-sm text-center px-4">
                            {card.name || card.displayName}
                        </h2>
                        {isStoreItem && (
                            Number(card.price) === 0 ? (
                                <div className="mt-2 bg-[#B4F73D]/10 backdrop-blur-md px-3 py-1 rounded-full border border-[#B4F73D]/30 flex items-center justify-center shadow-[0_0_15px_rgba(180,247,61,0.2)]">
                                    <span className="text-sm font-black text-[#B4F73D] uppercase tracking-widest drop-shadow-md">{t('free')}</span>
                                </div>
                            ) : (
                                <div className={cn("mt-2 bg-black/30 backdrop-blur-md px-3 py-1 rounded-full border border-white/5 flex items-center gap-1.5", isP2P ? "border-green-500/30" : "")}>
                                    <svg viewBox="0 0 24 24" className={cn("w-4 h-4", isP2P ? "fill-green-400" : "fill-[#3390EC]")}><path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-1-11v6h2v-6h-2zm-2.09-2.09L8.252 6.303A6.969 6.969 0 005.101 12h2.02c0-1.28.384-2.471 1.04-3.473l-.749-.617zM12 5.02c-1.358 0-2.618.385-3.692 1.041l.75.617C9.697 6.208 10.796 5.92 12 5.92V5.02z" /></svg>
                                    <span className="text-sm font-bold text-white">{card.price} GRAM</span>
                                </div>
                            )
                        )}
                    </div>
                </div>

                {/* Grid */}
                <div className="grid grid-cols-2 gap-3">
                    <DetailItem label={t('rarity')} value={`${card.rarity || card.rarity_score || 0}%`} icon={<Award size={14} className="text-yellow-400" />} />
                    <DetailItem label={t('rank')} value={card.rank || card.rank_name || t('commonRank')} />
                    <DetailItem label={t('package')} value={card.package_name || t('unknown')} />
                    {card.owner && <DetailItem label={t('owner')} value={card.owner} />}
                    {card.price > 0 && <DetailItem label={tc('price')} value={`${card.price} GRAM`} icon={<span className="text-blue-400 font-bold">💎</span>} />}
                    <DetailItem label={t('serial')} value={`#${card.serial_number || '---'}`} />
                    <DetailItem label={t('commission')} value="5%" />
                    <DetailItem label={t('evaluations')} value={card.likes_count ?? card.likes ?? 0} />
                    <DetailItem
                        label={t('supply')}
                        value={card.quantity
                            ? t('claimedCount', { sold: card.sold_count || 0, quantity: card.quantity })
                            : t('unlimited')}
                    />
                    <DetailItem label={tc('type')} value={isP2P ? t('userListing') : (isPaid ? t('exclusive') : (card.price === 0 ? t('free') : t('standard')))} />
                </div>

                {/* Price Valuation & Intelligence Engine (MySQL cached) */}
                {(card.name || card.displayName || card.username) && (
                    <UsernameValuationCard username={card.name || card.displayName || card.username} />
                )}

                {/* ID Block */}
                <div className="bg-[#dce0d9]/10 p-4 rounded-xl border border-white/5 backdrop-blur-sm">
                    <div className="flex justify-between items-center text-xs text-white/40 mb-1">
                        <span>{t('cardSerialNumber')}</span>
                    </div>
                    <div className="font-mono text-sm text-white/80 select-all tracking-wider">
                        #{card.serial_number || (card.sold_count || 0) + 1}
                    </div>
                </div>

                {/* Action Button */}
                {isStoreItem ? (
                    <button
                        onClick={() => {
                            impactOccurred('heavy');
                            onBuy?.();
                        }}
                        disabled={card.quantity === 0}
                        className={cn(
                            "w-full py-4 text-white font-bold rounded-xl transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg",
                            isP2P
                                ? "bg-green-500 hover:bg-green-600 shadow-green-500/20"
                                : "bg-[#3390EC] hover:bg-[#3390EC]/90 shadow-blue-500/20"
                        )}
                    >
                        {isP2P ? <Handshake size={20} /> : <ShoppingBag size={20} />}
                        {isP2P
                            ? t('makeOfferAmount', { price: card.price })
                            : (card.price > 0 ? t('buyForAmount', { price: card.price }) : t('claimFree'))
                        }
                    </button>
                ) : (
                    <p className="text-center text-xs text-white/30 pt-2">
                        {t('collectMoreCards')}
                    </p>
                )}
            </div>
        </Modal>
    );
}

function DetailItem({ label, value, icon, isStatus }: { label: string, value: string | number, icon?: React.ReactNode, isStatus?: boolean }) {
    return (
        <div className="bg-[#2c2c2e] p-3 rounded-xl border border-white/5 flex flex-col justify-between h-16">
            <span className="text-xs text-white/40 font-medium uppercase tracking-wide flex items-center gap-1">
                {icon} {label}
            </span>
            <span className={`text-sm font-bold ${isStatus && value === 'Active' ? 'text-green-400' : 'text-white'}`}>
                {value}
            </span>
        </div>
    );
}
